import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import defaultValidators from '@/helpers/default-validators';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import TextInput from '@/molecules/TextInput.vue';
import RadioRowItem from '@/molecules/RadioRowItem.vue';
import { updatePatient } from '@/services/patients';
import { getProSync } from '@/services/auth';
import { useMission } from '@/composables/useMissions';
import { getBirthdayLabel } from '@/helpers/patient';
import { requestMarkAsCompleted } from '@/services/missions';

const { update: updateMain, getById: getByIdParcours } = usePreventionParcours(
  '/cms/coordinator-patient-flu-and-vaccincation-infos',
);
const { update: updateFlu, getById: getFluById } = usePreventionParcours(
  '/cms/coordinator-patient-flu-infos',
);
const { update: updateCovid, getById: getCovidById } = usePreventionParcours(
  '/cms/coordinator-patient-vaccination-infos',
);
const { getById: getMissionById } = useMission();

const getCheckboxLabel = (data: any, lotKey: string, vaccinNameKey: string = 'vaccin_name') => {
  const pro = getProSync();
  const patient = data?.flowData?.patient;
  const mission = data?.flowData?.mission;
  const lot = data?.flowData?.[lotKey];
  const vaccinName = mission?.[vaccinNameKey];
  const now = new Date();

  const soussigne = pro?.civility === 'F' ? 'soussignée' : 'soussigné';
  const numType = pro?.rpps ? 'n° RPPS :' : 'n° ADELI :';
  const numValue = pro?.rpps || pro?.adeli;
  const nee = patient?.civility === 'F' ? 'née' : 'né';
  const birthStr = getBirthdayLabel(patient) || '-';
  const dateStr = now.toLocaleDateString('fr-FR');
  const timeStr = now.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `Je ${soussigne} ${pro?.lastname} ${pro?.firstname} (${numType} ${numValue}) certifie avoir vacciné ${patient?.firstname} ${patient?.lastname}, ${nee} le ${birthStr} avec le vaccin ${vaccinName} (n° de lot : ${lot}), le ${dateStr} à ${timeStr}`;
};

const getFluAttestationConfig = (data: any) => {
  const pro = getProSync();
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR');
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return [
    {
      section: 'Informations sur le patient',
      items: [
        { key: 'patient.lastname', label: 'Nom', source: 'flowData' },
        { key: 'patient.firstname', label: 'Prénom', source: 'flowData' },
        { label: 'Date de naissance', value: getBirthdayLabel(data?.flowData?.patient) || '-' },
      ],
    },
    {
      section: 'Informations sur le vaccin grippe',
      items: [
        {
          key: 'mission.vaccin_name',
          label: 'Type de vaccin',
          source: 'flowData',
        },
        { key: 'flu_lot_number', source: 'flowData', label: 'Numéro de lot' },
        {
          key: 'flu_vaccin_expiration_date',
          source: 'flowData',
          label: 'Date de péremption du vaccin',
          type: 'date',
        },
        {
          key: 'flu_vaccination_date_display',
          label: "Date d'administration du vaccin",
          value: dateStr,
        },
        {
          key: 'flu_vaccination_time_display',
          label: "Heure d'administration du vaccin",
          value: timeStr,
        },
      ],
    },
    {
      section: "Informations sur l'infirmier",
      items: [
        { key: 'pro_lastname', label: 'Nom', value: pro?.lastname },
        { key: 'pro_firstname', label: 'Prénom', value: pro?.firstname },
        {
          key: 'pro_number',
          label: pro?.rpps ? 'Numéro RPPS' : 'Numéro ADELI',
          value: pro?.rpps || pro?.adeli,
        },
      ],
    },
    {
      checkbox: true,
      label: getCheckboxLabel(data, 'flu_lot_number', 'vaccin_name'),
      errorMessage: "Vous devez certifier avoir vacciné ce patient avant d'envoyer l'attestation",
    },
  ];
};

const getCovidAttestationConfig = (data: any) => {
  const pro = getProSync();
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR');
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return [
    {
      section: 'Informations sur le patient',
      items: [
        { key: 'patient.lastname', label: 'Nom', source: 'flowData' },
        { key: 'patient.firstname', label: 'Prénom', source: 'flowData' },
        { label: 'Date de naissance', value: getBirthdayLabel(data?.flowData?.patient) || '-' },
      ],
    },
    {
      section: 'Informations sur le vaccin Covid 19',
      items: [
        {
          key: 'mission.vaccin_name_covid_for_covidxflu',
          label: 'Type de vaccin',
          source: 'flowData',
        },
        { key: 'covid_lot_number', source: 'flowData', label: 'Numéro de lot' },
        {
          key: 'covid_vaccin_expiration_date',
          source: 'flowData',
          label: 'Date de péremption du vaccin',
          type: 'date',
        },
        {
          key: 'covid_vaccination_date_display',
          label: "Date d'administration du vaccin",
          value: dateStr,
        },
        {
          key: 'covid_vaccination_time_display',
          label: "Heure d'administration du vaccin",
          value: timeStr,
        },
      ],
    },
    {
      section: "Informations sur l'infirmier",
      items: [
        { key: 'pro_lastname', label: 'Nom', value: pro?.lastname },
        { key: 'pro_firstname', label: 'Prénom', value: pro?.firstname },
        {
          key: 'pro_number',
          label: pro?.rpps ? 'Numéro RPPS' : 'Numéro ADELI',
          value: pro?.rpps || pro?.adeli,
        },
      ],
    },
    {
      checkbox: true,
      label: getCheckboxLabel(data, 'covid_lot_number', 'vaccin_name_covid_for_covidxflu'),
      errorMessage: "Vous devez certifier avoir vacciné ce patient avant d'envoyer l'attestation",
    },
  ];
};

const fluInfoVaccinationSchema = {
  validateOnChange: false,
  validateOnBlur: true,
  fields: [
    {
      id: 'flu_lot_number',
      type: 'text',
      as: TextInput,
      label: 'N° de Lot',
      rules: defaultValidators.text,
    },
    {
      id: 'flu_vaccin_expiration_date',
      type: 'datetime-local',
      as: TextInput,
      label: 'Date de péremption du vaccin',
      rules: defaultValidators.date,
    },
    {
      id: 'flu_injection_area',
      type: 'radio-group',
      as: RadioRowItem,
      label: "Zone d'injection",
      options: [
        { label: 'Bras droit', value: 'bras droit' },
        { label: 'Bras gauche', value: 'bras gauche' },
        { label: 'Autre', value: 'autre' },
      ],
      optionValue: 'value',
      optionLabel: (option) => option.label,
      rules: defaultValidators.radioGroup,
    },
  ],
};

const covidInfoVaccinationSchema = {
  validateOnChange: false,
  validateOnBlur: true,
  fields: [
    {
      id: 'covid_lot_number',
      type: 'text',
      as: TextInput,
      label: 'N° de Lot',
      rules: defaultValidators.text,
    },
    {
      id: 'covid_vaccin_expiration_date',
      type: 'datetime-local',
      as: TextInput,
      label: 'Date de péremption du vaccin',
      rules: defaultValidators.date,
    },
    {
      id: 'covid_injection_area',
      type: 'radio-group',
      as: RadioRowItem,
      label: "Zone d'injection",
      options: [
        { label: 'Bras droit', value: 'bras droit' },
        { label: 'Bras gauche', value: 'bras gauche' },
        { label: 'Autre', value: 'autre' },
      ],
      optionValue: 'value',
      optionLabel: (option) => option.label,
      rules: defaultValidators.radioGroup,
    },
  ],
};

export const vaccinationAndCovidFlow = withGlobalFlow({
  id: 'vaccinationAndCovidFlow',
  basePath: '/vaccinationAndCovidFlow/flow',
  exitRoute: '/vaccinationAndCovidFlow',

  async onInit({ route }) {
    const reservationId = route.params.reservationId;
    const missionId = route.params.missionId;
    const slotId = route.params.slotId;
    const [data, mission] = await Promise.all([
      getByIdParcours(reservationId),
      getMissionById(missionId),
    ]);
    const [fluData, covidData] = await Promise.all([
      data?.coordinator_patient_flu_info_id ? getFluById(reservationId) : null,
      data?.coordinator_patient_vaccination_id ? getCovidById(reservationId) : null,
    ]);

    return {
      ...data,
      mission,
      _missionId: missionId,
      _slotId: slotId,
      _reservationId: reservationId,
      ...(fluData
        ? {
            flu_lot_number: fluData.lot_number,
            flu_vaccin_expiration_date: fluData.vaccin_expiration_date,
            flu_injection_area: fluData.injection_area,
          }
        : {}),
      ...(covidData
        ? {
            covid_lot_number: covidData.lot_number,
            covid_vaccin_expiration_date: covidData.vaccin_expiration_date,
            covid_injection_area: covidData.injection_area,
          }
        : {}),
    };
  },

  steps: {
    isEligibleVaccination: {
      type: 'form',
      title: 'Éligibilité',
      description: 'Le patient est-il éligible à la vaccination ?',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'eligible',
            type: 'radio-group',
            as: RadioRowItem,
            options: [
              { label: 'Oui', value: true },
              { label: 'Non', value: false },
            ],
            optionValue: 'value',
            optionLabel: (option) => option.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },

    // ─── PARCOURS FLU ───
    fluInfo: {
      type: 'component',
      component: ListInfo,
      title: 'Vaccination antigrippale',
      props: (data: any) => ({
        data,
        config: [
          { key: 'mission.vaccin_name', label: 'Type de vaccin', source: 'flowData' },
          { label: "Mode d'administration", value: 'Intra-musculaire (IM)' },
        ],
      }),
    },

    fluInfoVaccination: {
      type: 'form',
      title: 'Information vaccin grippe',
      schema: fluInfoVaccinationSchema,
    },

    fluAttestationVaccination: {
      type: 'component',
      component: ListInfo,
      title: 'Attestation de vaccination grippe',
      props: (data: any) => ({
        data,
        config: getFluAttestationConfig(data),
      }),
    },

    // ─── PARCOURS COVID ───
    covidInfo: {
      type: 'component',
      component: ListInfo,
      title: 'Covid 19',
      props: (data: any) => ({
        data,
        config: [
          {
            key: 'mission.vaccin_name_covid_for_covidxflu',
            label: 'Type de vaccin',
            source: 'flowData',
          },
          { label: "Mode d'administration", value: 'Intra-musculaire (IM)' },
        ],
      }),
    },

    covidInfoVaccination: {
      type: 'form',
      title: 'Information vaccin Covid',
      schema: covidInfoVaccinationSchema,
    },

    covidAttestationVaccination: {
      type: 'component',
      component: ListInfo,
      title: 'Attestation de vaccination Covid',
      props: (data: any) => ({
        data,
        config: getCovidAttestationConfig(data),
      }),
    },
  },

  transitions: {
    isEligibleVaccination: {
      next: ({ data }) => {
        if (data.eligible === 'true' || data.eligible === true) {
          const type = data?.type;
          if (type === 'COVID19') return 'covidInfo';
          return 'fluInfo'; // FLU ou FLU-AND-COVID19 commence par flu
        }
        return {
          customSubmit: true,
          action: () => updatePatient(data?.patient?.id, { eligible: false }),
          redirect: `/missions/${data._missionId}/slots/${data._slotId}`,
        };
      },
    },

    // ─── FLU transitions ───
    fluInfo: {
      next: 'fluInfoVaccination',
    },

    fluInfoVaccination: {
      next: 'fluAttestationVaccination',
    },

    fluAttestationVaccination: {
      next: ({ data }) => {
        if (data?.type === 'FLU-AND-COVID19') return 'covidInfo';
        return { submit: true };
      },
    },

    // ─── COVID transitions ───
    covidInfo: {
      next: 'covidInfoVaccination',
    },

    covidInfoVaccination: {
      next: 'covidAttestationVaccination',
    },

    covidAttestationVaccination: {
      submit: true,
    },
  },

  async onSubmit(data) {
    const type = data?.type;
    const id = data?.id;
    const now = new Date().toISOString().replace('T', ' ').slice(0, 19);

    // Réservation passée en COMPLETED une seule fois, à la fin : un PUT identique
    // répété dans la même seconde ne modifie aucune ligne → 404 côté API.
    await updateMain(id, data, false, false);

    if (type === 'FLU' || type === 'FLU-AND-COVID19') {
      await updateFlu(
        data?.coordinator_patient_flu_info_id,
        {
          lot_number: data?.flu_lot_number,
          vaccin_expiration_date: data?.flu_vaccin_expiration_date,
          vaccination_date: now,
          injection_area: data?.flu_injection_area,
        },
        true,
        false,
      );
    }

    if (type === 'COVID19' || type === 'FLU-AND-COVID19') {
      await updateCovid(
        data?.coordinator_patient_vaccination_id,
        {
          lot_number: data?.covid_lot_number,
          vaccin_expiration_date: data?.covid_vaccin_expiration_date,
          vaccination_date: now,
          injection_area: data?.covid_injection_area,
        },
        true,
        false,
      );
    }

    await requestMarkAsCompleted(data?.reservation_id ?? data?._reservationId);
  },
});
